import { HttpTransportType, HubConnectionBuilder, HubConnectionState } from '@microsoft/signalr'
import type { HubConnection } from '@microsoft/signalr'

import type { ChatMessageDto } from '@/api/types/chat'
import { env } from '@/config/env'

/**
 * Сокеты чата. Историю и список тянет REST через прокси, живое приходит сюда.
 *
 * Хабов два, и это не наша прихоть: сообщения идут через `/chathub`, а о
 * закрытии обращения сервис сообщает событием `ChatClosed` на
 * `/notificationhub`. В обоих нужно войти в группу комнаты своим `JoinRoom`.
 *
 * Ключ хабам нужен на руках: подключение держит браузер, и проксировать его
 * нечем. Он статический и тот же, что зашит в мобильное приложение.
 */
export interface ChatSocketHandlers {
  message: (message: ChatMessageDto) => void
  /** Комната изменилась: пришло сообщение, оператор её просмотрел и т.п. */
  updated: (roomId: string) => void
  /** Оператор закрыл обращение — писать в него больше нельзя */
  closed: (roomId: string) => void
}

let chatHub: HubConnection | null = null
let notifyHub: HubConnection | null = null

/**
 * Комнаты, в группах которых мы состоим. Их несколько: пока приложение
 * открыто, слушаем все обращения клиента, а не только раскрытое на экране, —
 * иначе об ответе оператора на другом экране узнать неоткуда.
 */
const joined = new Set<string>()

/**
 * Слушатели отдельно от хабов: подписчиков несколько (лента переписки и
 * алерт о новом сообщении), а соединение одно на приложение.
 */
const listeners = new Set<ChatSocketHandlers>()

function build(path: string): HubConnection {
  return new HubConnectionBuilder()
    .withUrl(`${env.chatUrl}/${path}`, {
      accessTokenFactory: () => env.chatKey,
      /* Хабы предлагают и SSE с long-polling, но в вебе нам хватает сокета */
      transport: HttpTransportType.WebSockets,
      skipNegotiation: false,
    })
    .withAutomaticReconnect()
    .build()
}

/** Id комнаты сервис шлёт то строкой, то объектом — читаем оба варианта. */
function roomIdOf(payload: unknown): string {
  if (typeof payload === 'string') return payload

  const value = payload as { chatRoomId?: string; roomId?: string; id?: string } | null
  return value?.chatRoomId ?? value?.roomId ?? value?.id ?? ''
}

function emitMessage(message: ChatMessageDto): void {
  for (const listener of [...listeners]) listener.message(message)
}

function emitRoom(event: 'updated' | 'closed', roomId: string): void {
  for (const listener of [...listeners]) listener[event](roomId)
}

/**
 * Группы живут на сервере вместе с соединением: после обрыва хаб поднимается
 * с новым id и о наших комнатах уже не знает. Поэтому входим в них заново —
 * без этого чат после потери сети молча перестаёт получать сообщения.
 */
async function rejoin(hub: HubConnection): Promise<void> {
  for (const roomId of joined) await hub.invoke('JoinRoom', roomId).catch(() => undefined)
}

async function start(connection: HubConnection): Promise<boolean> {
  if (connection.state === HubConnectionState.Connected) return true

  try {
    await connection.start()
    return true
  } catch {
    return false
  }
}

/** Подключения живут одни на приложение — меняются только слушатели и комнаты. */
export async function connectChat(handlers: ChatSocketHandlers): Promise<boolean> {
  if (!env.chatUrl || !env.chatKey) return false

  listeners.add(handlers)

  if (!chatHub) {
    chatHub = build('chathub')
    chatHub.on('ReceiveMessage', (message: ChatMessageDto) => emitMessage(message))
    chatHub.on('ChatUpdated', (payload: unknown) => emitRoom('updated', roomIdOf(payload)))
    chatHub.onreconnected(() => void rejoin(chatHub as HubConnection))
  }

  if (!notifyHub) {
    notifyHub = build('notificationhub')
    notifyHub.on('ChatClosed', (payload: unknown) => emitRoom('closed', roomIdOf(payload)))
    notifyHub.onreconnected(() => void rejoin(notifyHub as HubConnection))
  }

  const [live] = await Promise.all([start(chatHub), start(notifyHub)])

  /* Уведомления — дополнение: без них чат работает, просто закрытие приедет позже */
  return live
}

export function disconnectChat(handlers: ChatSocketHandlers): void {
  listeners.delete(handlers)
}

/**
 * Вход в группы комнат. Подписка накопительная: экран переписки добавляет
 * свою комнату к тем, что уже слушает приложение, и выходя её не роняет.
 */
export async function joinRooms(roomIds: string[]): Promise<void> {
  for (const roomId of roomIds) {
    if (!roomId || joined.has(roomId)) continue

    joined.add(roomId)

    for (const hub of [chatHub, notifyHub]) {
      if (hub?.state !== HubConnectionState.Connected) continue
      await hub.invoke('JoinRoom', roomId).catch(() => undefined)
    }
  }
}

export async function leaveRooms(): Promise<void> {
  for (const roomId of joined) {
    for (const hub of [chatHub, notifyHub]) {
      if (hub?.state !== HubConnectionState.Connected) continue
      await hub.invoke('LeaveRoom', roomId).catch(() => undefined)
    }
  }
  joined.clear()
}

/**
 * Отправка через хаб: сервер сам разошлёт сообщение всем в комнате, включая
 * нас, — поэтому в ленту его кладёт обработчик `ReceiveMessage`, а не отправка.
 */
export async function sendMessage(
  roomId: string,
  senderId: string,
  senderType: number,
  content: string,
  attachmentIds: string[] | null = null,
): Promise<boolean> {
  if (chatHub?.state !== HubConnectionState.Connected) return false

  await chatHub.invoke('SendMessage', roomId, senderId, senderType, content, attachmentIds)
  return true
}

export function isChatConnected(): boolean {
  return chatHub?.state === HubConnectionState.Connected
}
